import React, { useEffect, useState } from 'react';

// Hooks
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { displayTooltip } from '~/hooks/displayTooltip';

//Utils
import {
  DOWNLOADABLE_BATCH_STATUSES,
  DEFAULT_PAGE_SIZES,
  DEFAULT_PAGINATION,
  REAL_PENNED_LETTER_PRODUCT_TYPE,
  PDF_REGENERATION_ALLOWED_STATUSES,
} from '../../../../utils/constants';
import { MESSAGES } from '../../../../utils/message';
import { downloadFile, stringLength } from '../../../../utils/helperFunctions';

// Components
import RegeneratePDFModal from '../RegeneratePDFModal';
import LinksModal from '../LinksModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// MUI Table
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  MenuItem,
  Typography,
  Tooltip,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/material';

// Icons
import { NoContactCIcon, CSVIcon, PDFIcon, DetailsCIcon, InfoCIcon } from '~/assets/images';
import NoContact from '../../../../assets/images/contacts/no-contact.svg';
import CachedIcon from '@mui/icons-material/Cached';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Styles
import './styles.scss';

const BatchTable = ({ pagination, updatePagination, searchApplied }) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [activeRowId, setActiveRowId] = useState(null);
  const [regeneratePDF, setRegeneratePDF] = useState({
    open: false,
    batchId: null,
  });

  const [linksModal, setLinksModal] = useState({
    open: false,
    batchId: null,
    loading: false,
    accutraceLinks: [],
  });

  const [downloading, setDownloading] = useState({
    status: false,
    batchId: null,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const pageSizes = DEFAULT_PAGE_SIZES;

  const rows = useSelector((state) => state.adminBatchesReducers.batches);

  const rowsCount = useSelector((state) => state.adminBatchesReducers.rowsCount);
  const to = useSelector((state) => state.adminBatchesReducers.to);
  const from = useSelector((state) => state.adminBatchesReducers.from);
  const lastPage = useSelector((state) => state.adminBatchesReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.adminBatchesReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.adminBatchesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const isLoading = useSelector((state) => state.adminBatchesReducers.loading);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleRegeneratePDF = (params) => {
    setRegeneratePDF({
      open: true,
      batchId: params.row.batchId,
    });
  };

  const handleCloseModal = () => {
    setRegeneratePDF({ open: false, batchId: null });
  };

  const isTooltip = displayTooltip();

  const isPDFRegenerationAllowed = (params) => {
    return (
      params.row.productType !== REAL_PENNED_LETTER_PRODUCT_TYPE &&
      (params.row.wooId || PDF_REGENERATION_ALLOWED_STATUSES.includes(params.row.batchStatus))
    );
  };

  const getTooltipTitle = (params) => {
    if (params.row.productType === REAL_PENNED_LETTER_PRODUCT_TYPE) {
      return 'PDF generation is not supported';
    } else if (isPDFRegenerationAllowed(params)) {
      return 'Regenerate PDF';
    } else if (params.row.wooId === null) {
      return 'Batch in progress';
    }
    return null;
  };

  const redirectPDFUrl = (pdfUrl) => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.target = '_blank';

    // Append the link to the document
    document.body.appendChild(link);

    // Trigger a click on the link
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  const downloadCSV = async (csvFileUrl, batchId) => {
    setDownloading({
      status: true,
      batchId: batchId,
    });

    await downloadFile(csvFileUrl, `Batch-${batchId}.csv`);
    handleMenuClose();

    setDownloading({
      status: false,
      batchId: null,
    });
  };

  const isBatchProcessed = (batchStatus) => {
    return DOWNLOADABLE_BATCH_STATUSES.includes(batchStatus);
  };

  const getPDFIconProps = (productType, pdfUrl, csvUrl, batchId) => {
    const isPDFDisabled = productType === REAL_PENNED_LETTER_PRODUCT_TYPE;

    return {
      pdf: {
        tooltipMessage: isPDFDisabled
          ? MESSAGES.ADMIN_DASHBOARD.BATCHES.PDF_GENERATION_NOT_SUPPORTED
          : MESSAGES.ADMIN_DASHBOARD.BATCHES.DOWNLOAD_PDF,
        iconStyle: {
          cursor: isPDFDisabled ? 'not-allowed' : 'pointer',
          opacity: isPDFDisabled ? 0.5 : 1,
          width: '20px',
          height: '20px',
        },
        handleClick: () => {
          if (!isPDFDisabled) redirectPDFUrl(pdfUrl);
        },
      },
      csv: {
        tooltipMessage: MESSAGES.ADMIN_DASHBOARD.BATCHES.DOWNLOAD_CSV,
        iconStyle: {
          cursor: 'pointer',
          opacity: 1,
          width: '20px',
          height: '20px',
        },
        handleClick: () => downloadCSV(csvUrl, batchId),
      },
    };
  };

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setActiveRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveRowId(null);
  };

  const handleLinksModalOpen = (batchId, accuTraceUrls) => {
    setLinksModal({ open: true, batchId: batchId, accutraceLinks: accuTraceUrls });
    handleMenuClose();
  };

  const handleLinksModalClose = () => {
    setLinksModal({ open: false, batchId: null });
  };

  const renderMenuItems = (params) => {
    const { pdf, csv } = getPDFIconProps(
      params?.row?.productType,
      params?.row?.pdfUrl,
      params?.row?.csvUrl,
      params?.row?.batchId
    );
    const canGeneratePDF = isPDFRegenerationAllowed(params);
    const isRealPennedLetter = params?.row?.productType === REAL_PENNED_LETTER_PRODUCT_TYPE;

    return (
      <OlcActionMenu
        id="long-menu"
        anchorEl={anchorEl}
        open={open && activeRowId === params.row.batchId}
        onClose={handleMenuClose}
      >
        {isBatchProcessed(params?.row?.batchStatus) && !isRealPennedLetter && (
          <MenuItem
            onClick={() => {
              pdf.handleClick();
              handleMenuClose();
            }}
            disabled={isRealPennedLetter}
          >
            <img src={PDFIcon} style={pdf.iconStyle} alt="PDF" />
            {pdf.tooltipMessage}
          </MenuItem>
        )}
        {isBatchProcessed(params?.row?.batchStatus) && (
          <MenuItem
            onClick={() => {
              csv.handleClick();
              handleMenuClose();
            }}
            disabled={downloading.status && downloading.batchId === params?.row?.batchId}
          >
            {downloading.status && downloading.batchId === params?.row?.batchId ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              <img src={CSVIcon} style={csv.iconStyle} alt="CSV" />
            )}
            {downloading.status && downloading.batchId === params?.row?.batchId
              ? 'Downloading'
              : csv.tooltipMessage}
          </MenuItem>
        )}
        {!isRealPennedLetter && (
          <MenuItem
            onClick={() => {
              if (canGeneratePDF) {
                handleRegeneratePDF(params);
              }
              handleMenuClose();
            }}
            disabled={!canGeneratePDF}
          >
            <CachedIcon
              sx={{
                opacity: canGeneratePDF ? 1 : 0.5,
                cursor: canGeneratePDF ? 'pointer' : 'not-allowed',
                fill: '#545454',
                stroke: '#ffffff',
                strokeWidth: '0.6px',
                width: '19px',
                height: '19px',
              }}
            />
            {getTooltipTitle(params)}
          </MenuItem>
        )}
        {Array.isArray(params?.row?.accuTraceUrls) && params?.row?.accuTraceUrls.length > 0 && (
          <MenuItem
            onClick={() => handleLinksModalOpen(params?.row?.batchId, params?.row?.accuTraceUrls)}
          >
            <img src={DetailsCIcon} style={csv.iconStyle} alt="Details" />
            View Accutrace Links
          </MenuItem>
        )}
      </OlcActionMenu>
    );
  };

  const columns = [
    {
      field: 'batchId',
      headerName: 'ID',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.batchId || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'wooId',
      headerName: 'Woo Order',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box
          className="addressWrapper"
          sx={{
            cursor: params.row.wooUrl && params.row.wooId ? 'pointer' : 'default',
          }}
        >
          {params.row.wooUrl && params.row.wooId ? (
            <Link to={params.row.wooUrl} className="link" target="_blank">
              <Typography style={{ display: 'flex', alignItems: 'center' }}>
                {params.row.wooId}
                {params?.row?.wooCommerceStatus && (
                  <Tooltip title={`${params?.row?.wooCommerceStatus || 'N/A'}`}>
                    <img src={InfoCIcon} alt="icon" style={{ marginLeft: '5px' }} />
                  </Tooltip>
                )}
              </Typography>
            </Link>
          ) : (
            <Typography>-</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'ordersCount',
      headerName: 'Orders',
      width: 100,
      sortable: false,
      headerClassName: 'batchCenteredHeader',
      renderCell: (params) => (
        <Box className="addressWrapper batchCenteredCell">
          <Link className="link" to={`/admin/orders/batch/${params.row.batchId}`}>
            <Typography>{params.row.ordersCount || ' -'}</Typography>
          </Link>
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      type: 'number',
      width: 100,
      sortable: false,
      headerClassName: 'batchCenteredHeader',
      renderCell: (params) => (
        <Box className="addressWrapper batchCenteredCell">
          <Typography>{params.row.quantity || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 130,
      sortable: false,
      headerClassName: 'batchCenteredHeader',
      renderCell: (params) => (
        <Box className="addressWrapper batchCenteredCell">
          <Typography>{params.row.revenue || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.createdAt || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'mailedDate',
      headerName: 'Mailing Date',
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.mailedDate || 'Pending'}</Typography>
        </Box>
      ),
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      width: 330,
      compactMaxWidth: 330,
      sortable: false,
      renderCell: (params) => (
        <Tooltip
          title={params.row.productType}
          disableHoverListener={params.row.productType.length < 37}
        >
          <Box className="productValWrapper">
            <Typography>{params.row.productType || ' -'}</Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'accuzipStatus',
      headerName: 'Accuzip Status',
      width: 200,
      sortable: false,
      renderCell: (params) =>
        stringLength(`${params.row.accuzipStatus}`, 20) ? (
          <Tooltip title={params.row.accuzipStatus} disableHoverListener={isTooltip}>
            <Box className="valWrapper">
              <Typography>{params.row.accuzipStatus || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="valWrapper">
            <Typography>{params.row.accuzipStatus || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'batchStatus',
      headerName: 'Batch Status',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box className="valWrapper">
          <Typography>{params.row.batchStatus || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      width: 120,
      compactWidth: 120,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="actionsWrapper">
            <Tooltip
              title={
                params.row.productType === REAL_PENNED_LETTER_PRODUCT_TYPE &&
                params.row.batchStatus === 'AccuZip processing not required'
                  ? 'No Actions Available'
                  : ''
              }
            >
              <IconButton
                aria-label="more"
                aria-controls={open && activeRowId === params.row.batchId ? 'long-menu' : undefined}
                aria-expanded={open && activeRowId === params.row.batchId ? 'true' : undefined}
                aria-haspopup="true"
                onClick={
                  params.row.productType === REAL_PENNED_LETTER_PRODUCT_TYPE &&
                  params.row.batchStatus === 'AccuZip processing not required'
                    ? null
                    : (event) => handleMenuClick(event, params.row.batchId)
                }
                sx={{
                  cursor:
                    params.row.productType === REAL_PENNED_LETTER_PRODUCT_TYPE &&
                    params.row.batchStatus === 'AccuZip processing not required'
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    params.row.productType === REAL_PENNED_LETTER_PRODUCT_TYPE &&
                    params.row.batchStatus === 'AccuZip processing not required'
                      ? 0.5
                      : 1,
                }}
              >
                <MoreVertIcon
                  sx={{
                    color: activeRowId === params.row.batchId ? '#ed5c2f' : 'inherit',
                  }}
                />
              </IconButton>
            </Tooltip>
            {renderMenuItems(params)}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="adminUserTable">
        <div className="batchTableWrapper olcAdminListTable">
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight={false}
            loading={isLoading}
            rowCount={rowsCount}
            pagination={pagination}
            paginationMode="server"
            hideFooterSelectedRowCount
            hideFooterPagination
            rowSelection={false}
            getRowId={(row) => row?.key}
            disableColumnMenu={true}
            className="ordersTableGrid"
            columnHeaderHeight={47}
            slots={{
              noRowsOverlay: () => (
                <Stack className="noRowsTextContacts">
                  <img src={NoContactCIcon} alt="NoContact" />
                  {`${MESSAGES.NO_RESULTS_TEXT} ${!searchApplied ? 'filters' : 'search'}`}
                </Stack>
              ),
              noResultsOverlay: () => (
                <Stack className="noRowsTextContacts">
                  <img src={NoContactCIcon} alt="NoContact" />
                  {MESSAGES.NO_ROWS_TEXT}
                </Stack>
              ),
            }}
          />
          {(isLoading || rowsCount > 0) && (
            <OlcTablePagination
              count={lastPage}
              page={currentPage}
              pageSize={perPage}
              pageSizes={pageSizes}
              onPageChange={handleChangePage}
              onPageSizeChange={(event) =>
                updatePagination({ page: 0, pageSize: event.target.value })
              }
              from={from}
              to={to}
              total={rowsCount}
              loading={isLoading}
            />
          )}
        </div>
      </div>
      <RegeneratePDFModal
        openModal={regeneratePDF.open}
        batchId={regeneratePDF.batchId}
        handleCloseModal={handleCloseModal}
        searchApplied={searchApplied}
        pagination={{
          page: currentPage,
          pageSize: perPage,
        }}
      />
      <LinksModal
        open={linksModal.open}
        batchId={linksModal.batchId}
        onClose={handleLinksModalClose}
        loading={linksModal.loading}
        accutraceLinks={linksModal.accutraceLinks}
      />
    </>
  );
};

export default BatchTable;
